import { z } from 'zod';
import {
  ExtractionMethodEnum,
  InvoiceDocumentTypeEnum,
  InvoiceFormatEnum,
  InvoiceStatusEnum,
} from './invoice-blob.js';

/**
 * Compact representation of an invoice for list endpoints (`GET /invoices`).
 * Excludes the full blob; full payload is fetched per-id by the detail route.
 */
export const InvoiceListItemSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  number: z.string().nullable(),
  issueDate: z.string().date().nullable(),
  sellerName: z.string().nullable(),
  documentType: InvoiceDocumentTypeEnum.nullable(),
  grossAmount: z.number().nullable(),
  currency: z.string().length(3).nullable(),
  marginAmount: z.number().nullable().default(null),
  marginPercent: z.number().nullable().default(null),
  status: InvoiceStatusEnum,
  format: InvoiceFormatEnum.nullable(),
  extractionMethod: ExtractionMethodEnum.nullable(),
  exceptionCategory: z
    .enum(['sap_matching_gate', 'manual_review', 'pipeline_error'])
    .nullable()
    .optional(),
  exceptionMessage: z.string().nullable().optional(),
  skontoDeadline: z.string().date().nullable().optional(),
  skontoDaysRemaining: z.number().int().nullable().optional(),
  skontoUrgency: z.enum(['overdue', 'due_today', 'due_soon']).nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().optional(),
});
export type InvoiceListItem = z.infer<typeof InvoiceListItemSchema>;

export const PaginationMetaSchema = z.object({
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});
export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;

export const InvoiceListResponseSchema = z.object({
  items: z.array(InvoiceListItemSchema),
  pagination: PaginationMetaSchema,
});
export type InvoiceListResponse = z.infer<typeof InvoiceListResponseSchema>;

/**
 * Query filters supported by the list route. All fields are optional;
 * defaults are applied server-side.
 */
export const InvoiceFiltersSchema = z.object({
  tenantId: z.string().optional(),
  documentType: InvoiceDocumentTypeEnum.optional(),
  status: InvoiceStatusEnum.optional(),
  format: InvoiceFormatEnum.optional(),
  /** Substring match on seller name (case-insensitive). */
  sellerSearch: z.string().optional(),
  issueDateFrom: z.string().date().optional(),
  issueDateTo: z.string().date().optional(),
  limit: z.coerce.number().int().positive().max(200).default(20),
  offset: z.coerce.number().int().nonnegative().default(0),
});
export type InvoiceFilters = z.infer<typeof InvoiceFiltersSchema>;

/**
 * Sortable list columns (#42). The set is intentionally narrow: the four
 * columns the list UI exposes as sort headers plus `status` (used by the
 * status filter section). Adding "raw" columns here is fine; they will be
 * mapped to a Drizzle `orderBy` expression on the backend side.
 */
export const InvoiceSortFieldEnum = z.enum([
  'createdAt',
  'issueDate',
  'grossAmount',
  'sellerName',
  'status',
]);
export type InvoiceSortField = z.infer<typeof InvoiceSortFieldEnum>;

export const InvoiceSortOrderEnum = z.enum(['asc', 'desc']);
export type InvoiceSortOrder = z.infer<typeof InvoiceSortOrderEnum>;

/**
 * Filters + sort merged into the wire-shape that `GET /invoices` accepts as
 * query string. Sort defaults to `createdAt` desc so the list shows the
 * newest uploads first when the UI does not specify an order. The frontend
 * mirrors these defaults in its `validateSearch` schema so an empty URL
 * surfaces the same data as a fully-specified one.
 */
export const InvoiceQuerySchema = InvoiceFiltersSchema.extend({
  sort: InvoiceSortFieldEnum.default('createdAt'),
  order: InvoiceSortOrderEnum.default('desc'),
});
export type InvoiceQuery = z.infer<typeof InvoiceQuerySchema>;
